import {
  ConsentManagerDialog,
  ConsentManagerProvider,
  CookieBanner,
} from '@c15t/react'
import type { PropsWithChildren } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { useAuth } from '@/hooks/useAuth'
import { recordConsentChange } from '@/services/CookieConsentService'
import { gtmService } from '@/services/GTMService'
import { postHogTrackingService } from '@/services/PostHogTrackingService'

const COOKIE_POLICY_PATH = 'https://praxis.emergn.com/cookie-policy'

const CookieBannerDescription = () => (
  <>
    <p className="mb-2">
      This site uses cookies to improve your browsing experience, analyze site
      traffic, and show personalized content.
    </p>
    <p>
      For more information on the cookies we use, please see our{' '}
      <a
        href={COOKIE_POLICY_PATH}
        className="text-(--legal-links-color) hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        Cookie Policy
      </a>
      {'.'}
    </p>
  </>
)

export const CookieManagerProvider = ({ children }: PropsWithChildren) => {
  const { user, isLoaded, getToken } = useAuth()

  const hasInitializedRef = useRef(false)
  const userIdRef = useRef<string | null>(user?.id ?? null)

  useEffect(() => {
    if (isLoaded) {
      userIdRef.current = user?.id ?? null
    }
  }, [user, isLoaded])

  const handleConsentSet = useCallback(
    ({ preferences }: { preferences: Record<string, boolean> }) => {
      postHogTrackingService.updateConsent(preferences)
      gtmService.updateConsent(preferences)

      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true
        return
      }

      const userId = userIdRef.current

      recordConsentChange({
        userId,
        preferences,
        getToken: userId ? getToken : undefined,
      }).catch((error) => {
        console.error('Failed to record cookie consent change', error)
      })
    },
    [getToken]
  )

  return (
    <ConsentManagerProvider
      options={{
        mode: 'offline',
        consentCategories: ['necessary', 'measurement', 'marketing'],
        callbacks: {
          onConsentSet: handleConsentSet,
        },
      }}
    >
      <CookieBanner
        description={<CookieBannerDescription />}
        theme={{
          'banner.footer.sub-group': 'gap-2!',
          'banner.footer.customize-button':
            'bg-primary! text-primary-foreground! hover:bg-primary-dark!',
        }}
      />
      <ConsentManagerDialog
        theme={{
          'dialog.footer': 'hidden! invisible',
          'widget.footer.sub-group': 'gap-2!',
        }}
      />
      {children}
    </ConsentManagerProvider>
  )
}
